import { ArrowDownMini as ArrowDown, ArrowUpMini as ArrowUp, Eye, EyeSlash, Plus, Trash } from "@medusajs/icons"
import { storefrontUrl } from "@/lib/storefront-url"
import { Button, DropdownMenu, IconButton, Input, Select, Text, Textarea, toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { FilePicker } from "@/components/file-picker"
import { Field } from "@/components/form"
import { RichTextEditor } from "@/components/rich-text"
import { ErrorState, PageHeader, SaveBar, Section as Card } from "@/components/ui"
import { newSection, SECTION_LABELS, useHomepage, useSaveHomepage, type Section, type SectionType } from "@/lib/homepage"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"

const badUrl = (v: string) => v !== "" && !/^(https:\/\/\S+|\/(?![/\\])\S*|http:\/\/localhost(:\d+)?\/\S*)$/i.test(v)
const summary = (s: Section) => {
  const x = s.settings as Record<string, unknown>
  return String(x.heading || x.title || x.text || (s.type === "trust" ? `${(s.settings as { items: unknown[] }).items.length} badge${(s.settings as { items: unknown[] }).items.length === 1 ? "" : "s"}` : "")) || "Not set up yet"
}

function ImageField({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <Field label="Image" hint={badUrl(value) ? "Use an https:// link or a /path" : undefined}>
      <div className="flex flex-col gap-2">
        <Input value={value} maxLength={500} placeholder="https://…" disabled={disabled} onChange={(e) => onChange(e.target.value.trim())} aria-invalid={badUrl(value)} />
        {value && !badUrl(value) && <img src={value} alt="" className="max-h-32 w-fit rounded-lg border border-ui-border-base object-cover" />}
        {!disabled && <div><Button type="button" size="small" variant="secondary" onClick={() => setOpen(true)}>Select from Files</Button></div>}
        {open && <FilePicker title="Select image" onClose={() => setOpen(false)} onDone={([f]) => { setOpen(false); onChange(f.url) }} />}
      </div>
    </Field>
  )
}

const LinkField = ({ label, value, onChange, disabled }: { label: string; value: string; onChange: (v: string) => void; disabled: boolean }) => (
  <Field label={label} hint={badUrl(value) ? "Use an https:// link or a /path such as /collections/all" : undefined}>
    <Input value={value} maxLength={500} placeholder="/collections/all" disabled={disabled} onChange={(e) => onChange(e.target.value.trim())} aria-invalid={badUrl(value)} />
  </Field>
)

function AlignSelect({ value, onChange, disabled, label = "Alignment", options = ["left", "center"] }: { value: string; onChange: (v: string) => void; disabled: boolean; label?: string; options?: string[] }) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <Select.Trigger><Select.Value /></Select.Trigger>
        <Select.Content>{options.map((o) => <Select.Item key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</Select.Item>)}</Select.Content>
      </Select>
    </Field>
  )
}

function SectionForm({ s, set, disabled }: { s: Section; set: (s: Section) => void; disabled: boolean }) {
  const up = <K extends string>(k: K, v: unknown) => set({ ...s, settings: { ...s.settings, [k]: v } } as Section)
  const g = s.settings as any
  switch (s.type) {
    case "announcement":
      return (<>
        <Field label="Text"><Input value={g.text} maxLength={160} disabled={disabled} onChange={(e) => up("text", e.target.value)} /></Field>
        <LinkField label="Link (optional)" value={g.link} onChange={(v) => up("link", v)} disabled={disabled} />
      </>)
    case "hero":
      return (<>
        <Field label="Heading"><Input value={g.heading} maxLength={120} disabled={disabled} onChange={(e) => up("heading", e.target.value)} /></Field>
        <Field label="Subheading"><Textarea rows={2} value={g.subheading} maxLength={300} disabled={disabled} onChange={(e) => up("subheading", e.target.value)} /></Field>
        <ImageField value={g.image} onChange={(v) => up("image", v)} disabled={disabled} />
        <Field label="Button label"><Input value={g.button_label} maxLength={40} disabled={disabled} onChange={(e) => up("button_label", e.target.value)} /></Field>
        <LinkField label="Button link" value={g.button_link} onChange={(v) => up("button_link", v)} disabled={disabled} />
        <AlignSelect value={g.align} onChange={(v) => up("align", v)} disabled={disabled} />
      </>)
    case "featured_collection":
      return (<>
        <Field label="Heading"><Input value={g.title} maxLength={80} disabled={disabled} onChange={(e) => up("title", e.target.value)} /></Field>
        <Field label="Collection handle" hint="Leave blank to show all products. The handle is the last part of the collection's URL.">
          <Input value={g.collection_handle} maxLength={120} placeholder="summer-sale" disabled={disabled} onChange={(e) => up("collection_handle", e.target.value.trim().toLowerCase())} aria-invalid={!/^[a-z0-9-]*$/.test(g.collection_handle)} />
        </Field>
        <Field label="Products to show"><Input type="number" min={2} max={12} value={g.count} disabled={disabled} onChange={(e) => up("count", Math.min(12, Math.max(2, Number(e.target.value) || 2)))} /></Field>
      </>)
    case "image_with_text":
      return (<>
        <Field label="Heading"><Input value={g.heading} maxLength={120} disabled={disabled} onChange={(e) => up("heading", e.target.value)} /></Field>
        <Field label="Text"><Textarea rows={4} value={g.text} maxLength={1000} disabled={disabled} onChange={(e) => up("text", e.target.value)} /></Field>
        <ImageField value={g.image} onChange={(v) => up("image", v)} disabled={disabled} />
        <Field label="Button label"><Input value={g.button_label} maxLength={40} disabled={disabled} onChange={(e) => up("button_label", e.target.value)} /></Field>
        <LinkField label="Button link" value={g.button_link} onChange={(v) => up("button_link", v)} disabled={disabled} />
        <AlignSelect label="Image position" options={["left", "right"]} value={g.image_side} onChange={(v) => up("image_side", v)} disabled={disabled} />
      </>)
    case "rich_text":
      return (<>
        <Field label="Heading"><Input value={g.heading} maxLength={120} disabled={disabled} onChange={(e) => up("heading", e.target.value)} /></Field>
        <RichTextEditor label="Content" value={g.body_html} onChange={(v) => up("body_html", v)} minHeight={140} />
        <AlignSelect value={g.align} onChange={(v) => up("align", v)} disabled={disabled} />
      </>)
    case "faq":
      return (<>
        <Field label="Heading"><Input value={g.title} maxLength={80} disabled={disabled} onChange={(e) => up("title", e.target.value)} /></Field>
        <Repeater items={g.items} max={20} addLabel="Add question" disabled={disabled} blank={{ q: "", a: "" }} onChange={(v) => up("items", v)}
          render={(it: { q: string; a: string }, put) => (<>
            <Input aria-label="Question" placeholder="Question" value={it.q} maxLength={200} disabled={disabled} onChange={(e) => put({ ...it, q: e.target.value })} />
            <Textarea aria-label="Answer" placeholder="Answer" rows={2} value={it.a} maxLength={1500} disabled={disabled} onChange={(e) => put({ ...it, a: e.target.value })} />
          </>)} />
      </>)
    case "trust":
      return (
        <Repeater items={g.items} max={6} addLabel="Add badge" disabled={disabled} blank={{ title: "", text: "" }} onChange={(v) => up("items", v)}
          render={(it: { title: string; text: string }, put) => (<>
            <Input aria-label="Title" placeholder="Title" value={it.title} maxLength={60} disabled={disabled} onChange={(e) => put({ ...it, title: e.target.value })} />
            <Input aria-label="Description" placeholder="Description" value={it.text} maxLength={160} disabled={disabled} onChange={(e) => put({ ...it, text: e.target.value })} />
          </>)} />
      )
  }
}

function Repeater<T>({ items, max, addLabel, blank, disabled, onChange, render }: { items: T[]; max: number; addLabel: string; blank: T; disabled: boolean; onChange: (v: T[]) => void; render: (it: T, put: (v: T) => void) => React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-2 rounded-lg border border-ui-border-base p-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">{render(it, (v) => onChange(items.map((x, j) => (j === i ? v : x))))}</div>
          {!disabled && <IconButton size="small" variant="transparent" aria-label="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash /></IconButton>}
        </div>
      ))}
      {!disabled && items.length < max && <div><Button type="button" size="small" variant="secondary" onClick={() => onChange([...items, blank])}><Plus />{addLabel}</Button></div>}
    </div>
  )
}

/** Online Store → Customize: the homepage as an ordered list of sections. */
export function CustomizePage() {
  const { data, error } = useHomepage()
  const save = useSaveHomepage()
  const writable = useCan()("content:write")
  const [list, setList] = useState<Section[] | null>(null)
  const [sel, setSel] = useState<string | null>(null)
  useEffect(() => {
    if (data) { setList(data.sections); setSel((c) => c ?? data.sections[0]?.id ?? null) }
  }, [data])
  if (error) return <ErrorState error={error} />
  if (!list || !data) return <Text size="small">Loading…</Text>
  const dirty = JSON.stringify(list) !== JSON.stringify(data.sections)
  const cur = list.find((s) => s.id === sel) ?? null
  const move = (i: number, d: number) => { const n = [...list]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setList(n) }
  const patch = (s: Section) => setList(list.map((x) => (x.id === s.id ? s : x)))
  const add = (t: SectionType) => { const s = newSection(t); setList([...list, s]); setSel(s.id) }
  const remove = (id: string) => { const n = list.filter((s) => s.id !== id); setList(n); setSel(n[0]?.id ?? null) }
  const invalid = list.some((s) => Object.values(s.settings as Record<string, unknown>).some((v) => typeof v === "string" && /^(javascript|data):/i.test(v)) || ["link", "button_link", "image"].some((k) => badUrl(String((s.settings as any)[k] ?? ""))))
  const hasAnn = list.some((s) => s.type === "announcement")
  return (
    <>
      <PageHeader
        title="Customize homepage"
        actions={<a href={storefrontUrl()} target="_blank" rel="noreferrer" className="txt-compact-small-plus rounded-lg bg-[#303030] px-3 py-1.5 text-white hover:bg-[#1a1a1a]">View your store</a>}
      />
      <div className="grid gap-4 pb-24 md:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
        <Card title="Sections">
          <ul className="divide-y divide-ui-border-base">
            {list.map((s, i) => (
              <li key={s.id} className={`flex items-center gap-1 px-2 py-1.5 ${sel === s.id ? "bg-ui-bg-base-pressed" : ""}`}>
                <button type="button" className="min-w-0 flex-1 px-2 py-1 text-left" onClick={() => setSel(s.id)} aria-current={sel === s.id}>
                  <div className={`truncate text-[13px] font-medium ${s.hidden ? "text-ui-fg-muted" : ""}`}>{SECTION_LABELS[s.type]}</div>
                  <div className="truncate text-xs text-ui-fg-subtle">{summary(s)}</div>
                </button>
                {writable && (<>
                  <IconButton size="small" variant="transparent" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp /></IconButton>
                  <IconButton size="small" variant="transparent" aria-label="Move down" disabled={i === list.length - 1} onClick={() => move(i, 1)}><ArrowDown /></IconButton>
                  <IconButton size="small" variant="transparent" aria-label={s.hidden ? "Show section" : "Hide section"} onClick={() => patch({ ...s, hidden: !s.hidden })}>{s.hidden ? <EyeSlash /> : <Eye />}</IconButton>
                </>)}
              </li>
            ))}
            {!list.length && <li className="p-4 text-[13px] text-ui-fg-subtle">No sections. Your homepage will be empty.</li>}
          </ul>
          {writable && list.length < 20 && (
            <div className="border-t border-ui-border-base p-2">
              <DropdownMenu>
                <DropdownMenu.Trigger asChild><Button size="small" variant="transparent"><Plus />Add section</Button></DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  {(Object.keys(SECTION_LABELS) as SectionType[]).filter((t) => t !== "announcement" || !hasAnn).map((t) => (
                    <DropdownMenu.Item key={t} onClick={() => add(t)}>{SECTION_LABELS[t]}</DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu>
            </div>
          )}
        </Card>
        {cur ? (
          <Card title={SECTION_LABELS[cur.type]}>
            <div className="flex flex-col gap-3 p-4" key={cur.id}>
              {cur.hidden && <Text size="small" className="text-ui-fg-subtle">This section is hidden from your store.</Text>}
              <SectionForm s={cur} set={patch} disabled={!writable} />
              {writable && <div className="pt-2"><Button type="button" size="small" variant="danger" onClick={() => remove(cur.id)}><Trash />Remove section</Button></div>}
            </div>
          </Card>
        ) : (
          <Card title="Section"><Text size="small" className="p-4 text-ui-fg-subtle">Select a section to edit it.</Text></Card>
        )}
      </div>
      <SaveBar visible={writable && dirty} message="Unsaved changes" onDiscard={() => { setList(data.sections); setSel(data.sections[0]?.id ?? null) }} saving={save.isPending} disabled={invalid}
        onSave={() => save.mutate(list, { onSuccess: () => toast.success("Homepage saved"), onError: (e) => toast.error(errorMessage(e)) })} />
    </>
  )
}
